/**
 * Photo-grid items are not always photos: since 2026-08 a drukattest may be
 * uploaded as a PDF (a pressure-test device emits one directly). A PDF cannot be
 * rendered by an `<img>` — it would show as a broken thumbnail — so every grid
 * that can hold one must branch on this.
 *
 * Why the extension is a sound signal, given the upload gate checks the MIME
 * type and never the filename: the API normalises the two before it writes the
 * key. `ensureUploadExtension()` in `portal-api/src/utils/upload-mime.ts` appends
 * the extension belonging to the validated type, so a PDF uploaded as `attest`
 * is stored as `attest.pdf`. Without that, this function would call it an image
 * and the grid would render a broken `<img>`. The photo listings return no
 * content type, so there is nothing else to key on — the two halves have to be
 * read together.
 *
 * Prefer the key over the filename: the filename is optimistic client state
 * right after an upload, the key always comes from the server.
 */
export function isPdfPhoto(item: { key?: string; filename?: string }): boolean {
  const name = item.key || item.filename || '';
  return name.toLowerCase().endsWith('.pdf');
}

/** True when the browser must skip canvas compression for this file. */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * The types the API accepts on photo-style uploads.
 *
 * Mirror of `PHOTO_UPLOAD_TYPES` in `portal-api/src/utils/upload-mime.ts`. The
 * API stays the authority — this copy exists so a user who picks a `.docx` is
 * told why before a junk file crosses the network.
 */
export const PHOTO_UPLOAD_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
];

/** Dutch, user-facing. Kept identical to the API's PHOTO_UPLOAD_TYPES_MESSAGE. */
export const PHOTO_UPLOAD_TYPES_MESSAGE = 'Alleen JPEG, PNG, WEBP, HEIC of PDF toegestaan';

/**
 * True for an iPhone photo in the HEIF family.
 *
 * Both halves of the test matter. Safari reports `image/heic`, but Chrome and
 * Firefox on Windows have no decoder for the format and often report an empty
 * `file.type` for it, so the extension is the only signal left. `image/heif`
 * and the `-sequence` variants exist too and decode the same way.
 */
export function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return /^image\/hei[cf]/.test(file.type) || name.endsWith('.heic') || name.endsWith('.heif');
}

/**
 * True only when the browser reported a type the API will certainly reject.
 *
 * Deliberately conservative: an empty `file.type` (some platforms report nothing
 * for HEIC) returns false, so we never pre-reject something the server might
 * have taken. In that case the request goes out and the API's own message is
 * what the user sees.
 *
 * HEIF is never pre-rejected either, including `image/heif`, which is not in the
 * mirrored list above: `compressImage` transcodes the whole family to JPEG in
 * the browser, so what reaches the API is a JPEG regardless of what was picked.
 */
export function isRejectedPhotoUploadType(file: File): boolean {
  if (isHeicFile(file)) return false;
  return file.type !== '' && !PHOTO_UPLOAD_TYPES.includes(file.type);
}

/**
 * An upload failure whose `message` is Dutch and meant for the user to read.
 *
 * The distinction exists because most of what can be thrown on this path is
 * not: the canvas says "Failed to load image", fetch says "NetworkError when
 * attempting to fetch resource". `describeUploadFailures` shows the message of
 * one of these and the bare count of anything else, so a new reason becomes
 * user-visible by subclassing here and nowhere else.
 */
export class UserFacingUploadError extends Error {}

/**
 * Thrown before upload for a type the API would reject anyway.
 *
 * Carries the Dutch reason as its `message` so the generic failure counter can
 * show it. Without this the browser failed first and opaquely: `compressImage`
 * fed the HTML to `new Image()`, `onerror` fired, and the user saw only
 * "1 bestand niet geüpload" with no reason and no mention of what is allowed.
 */
export class UnsupportedUploadTypeError extends UserFacingUploadError {
  constructor() {
    super(PHOTO_UPLOAD_TYPES_MESSAGE);
    this.name = 'UnsupportedUploadTypeError';
  }
}

/**
 * Thrown when a HEIC survived neither the browser's own decoder nor libheif.
 *
 * The honest reason, in place of the bare count the canvas failure used to
 * produce: a HEIC picked in a browser without a decoder failed with the English
 * "Failed to load image", which is dropped before the screen.
 */
export class HeicDecodeError extends UserFacingUploadError {
  constructor() {
    super('HEIC-foto kon niet worden omgezet in deze browser. Bewaar ze als JPEG en probeer opnieuw.');
    this.name = 'HeicDecodeError';
  }
}

/** Thrown when the browser cannot decode a file that claims to be an image. */
export class ImageDecodeError extends UserFacingUploadError {
  constructor() {
    super('Bestand kon niet worden gelezen als afbeelding');
    this.name = 'ImageDecodeError';
  }
}
